import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, MapPin, Clock, Facebook, Instagram, Twitter, Sparkles, Heart, Star, Calendar } from 'lucide-react';

const Template3 = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { 
      name: 'Hair Styling', 
      description: 'Expert cuts, coloring, and treatments for stunning hair',
      price: 'From $50', 
      icon: '💇‍♀️',
      image: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=400&h=300&fit=crop'
    },
    { 
      name: 'Facial Treatments', 
      description: 'Rejuvenating facials for glowing, healthy skin',
      price: 'From $80', 
      icon: '✨',
      image: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400&h=300&fit=crop'
    },
    { 
      name: 'Massage Therapy', 
      description: 'Relaxing massages to relieve stress and tension',
      price: 'From $70', 
      icon: '💆‍♀️',
      image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=400&h=300&fit=crop'
    },
    { 
      name: 'Manicure & Pedicure', 
      description: 'Beautiful nails with premium polish and care',
      price: 'From $40', 
      icon: '💅',
      image: 'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=400&h=300&fit=crop'
    },
    { 
      name: 'Spa Packages', 
      description: 'Complete relaxation with our signature spa experiences',
      price: 'From $150', 
      icon: '🌸',
      image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=400&h=300&fit=crop'
    },
    { 
      name: 'Makeup Artistry', 
      description: 'Professional makeup for any occasion',
      price: 'From $60', 
      icon: '💄',
      image: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=400&h=300&fit=crop'
    },
  ];

  const gallery = [
    'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1519415510236-718bdfcd89c8?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1516975080664-ed2fc6a32937?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1522338242992-e1a54906a8da?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1529290130-4ca3753253ae?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&fit=crop',
  ];

  const testimonials = [
    { name: 'Sarah Johnson', text: 'Absolutely amazing experience! The staff is professional and the atmosphere is so relaxing.', rating: 5 },
    { name: 'Emily Davis', text: 'Best facial I\'ve ever had. My skin has never looked better!', rating: 5 },
    { name: 'Jessica Wilson', text: 'Love this spa! Always leave feeling refreshed and beautiful.', rating: 5 },
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#ffffff',
      color: '#1f2937',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    header: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      transition: 'all 0.3s ease',
      background: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      boxShadow: scrolled ? '0 4px 30px rgba(236, 72, 153, 0.1)' : '0 2px 10px rgba(0, 0, 0, 0.05)',
    },
    nav: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '1.25rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    logo: {
      fontSize: '1.75rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #ec4899, #db2777)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    navLinks: {
      display: 'flex',
      gap: '2rem',
      alignItems: 'center',
    },
    navButton: {
      background: 'none',
      border: 'none',
      color: '#1f2937',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
      fontSize: '1rem',
      fontWeight: '500',
      fontFamily: 'inherit',
    },
    bookButton: {
      background: 'linear-gradient(135deg, #ec4899, #db2777)',
      color: 'white',
      border: 'none',
      padding: '0.75rem 1.75rem',
      borderRadius: '50px',
      cursor: 'pointer',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    menuToggle: {
      display: 'none',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
    },
    heroSection: {
      background: 'linear-gradient(135deg, #fce7f3, #fbcfe8)',
      padding: '10rem 2rem 6rem',
      position: 'relative',
      overflow: 'hidden',
    },
    heroContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '4rem',
      alignItems: 'center',
    },
    heroText: {
      zIndex: 10,
    },
    heroTitle: {
      fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
      fontWeight: '800',
      color: '#831843',
      marginBottom: '1.5rem',
      lineHeight: 1.1,
    },
    heroSubtitle: {
      fontSize: '1.25rem',
      color: '#9f1239',
      marginBottom: '2rem',
      lineHeight: 1.6,
    },
    heroFeatures: {
      display: 'flex',
      gap: '1.5rem',
      flexWrap: 'wrap',
      marginBottom: '2rem',
    },
    feature: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      background: 'white',
      padding: '0.75rem 1.25rem',
      borderRadius: '30px',
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#831843',
      boxShadow: '0 4px 15px rgba(236, 72, 153, 0.15)',
    },
    ctaButton: {
      background: 'linear-gradient(135deg, #ec4899, #db2777)',
      color: 'white',
      border: 'none',
      padding: '1.1rem 2.5rem',
      borderRadius: '50px',
      cursor: 'pointer',
      fontSize: '1.1rem',
      fontWeight: '700',
      transition: 'all 0.3s ease',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.75rem',
      boxShadow: '0 8px 25px rgba(236, 72, 153, 0.3)',
    },
    heroImage: {
      position: 'relative',
    },
    heroImg: {
      width: '100%',
      borderRadius: '30px',
      boxShadow: '0 25px 70px rgba(236, 72, 153, 0.3)',
    },
    floatingCard: {
      position: 'absolute',
      background: 'white',
      padding: '1.5rem',
      borderRadius: '20px',
      boxShadow: '0 15px 40px rgba(0, 0, 0, 0.15)',
      animation: 'float 3s ease-in-out infinite',
    },
    section: {
      padding: '5rem 2rem',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    sectionTitle: {
      fontSize: 'clamp(2rem, 4vw, 3rem)',
      fontWeight: '800',
      color: '#831843',
      marginBottom: '1rem',
      textAlign: 'center',
    },
    sectionSubtitle: {
      fontSize: '1.15rem',
      color: '#9f1239',
      marginBottom: '3rem',
      textAlign: 'center',
      maxWidth: '600px',
      margin: '0 auto 3rem',
    },
    servicesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: '2rem',
    },
    serviceCard: {
      background: 'white',
      borderRadius: '20px',
      overflow: 'hidden',
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.4s ease',
      cursor: 'pointer',
      border: '2px solid transparent',
    },
    serviceImage: {
      width: '100%',
      height: '220px',
      objectFit: 'cover',
      transition: 'transform 0.4s ease',
    },
    serviceContent: {
      padding: '2rem',
    },
    serviceIcon: {
      fontSize: '2.5rem',
      marginBottom: '1rem',
      display: 'block',
    },
    serviceName: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#831843',
      marginBottom: '0.75rem',
    },
    serviceDesc: {
      color: '#6b7280',
      marginBottom: '1rem',
      lineHeight: 1.6,
    },
    servicePrice: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: '#ec4899',
      marginBottom: '1rem',
    },
    serviceButton: {
      width: '100%',
      background: 'linear-gradient(135deg, #ec4899, #db2777)',
      color: 'white',
      border: 'none',
      padding: '0.875rem',
      borderRadius: '12px',
      cursor: 'pointer',
      fontWeight: '600',
      transition: 'all 0.3s ease',
    },
    galleryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '1.5rem',
    },
    galleryItem: {
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '20px',
      aspectRatio: '1',
      cursor: 'pointer',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
    },
    galleryImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.5s ease',
    },
    galleryOverlay: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(to top, rgba(236, 72, 153, 0.9), transparent)',
      opacity: 0,
      transition: 'opacity 0.3s ease',
      display: 'flex',
      alignItems: 'flex-end',
      padding: '1.5rem',
      color: 'white',
      fontWeight: '600',
    },
    testimonialsSection: {
      background: 'linear-gradient(135deg, #fce7f3, #fbcfe8)',
    },
    testimonialsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '2rem',
    },
    testimonialCard: {
      background: 'white',
      padding: '2.5rem',
      borderRadius: '20px',
      boxShadow: '0 8px 30px rgba(236, 72, 153, 0.15)',
      position: 'relative',
    },
    testimonialText: {
      fontSize: '1.05rem',
      color: '#4b5563',
      lineHeight: 1.7,
      marginBottom: '1.5rem',
      fontStyle: 'italic',
    },
    testimonialAuthor: {
      fontWeight: '700',
      color: '#831843',
      fontSize: '1.1rem',
    },
    rating: {
      display: 'flex',
      gap: '0.25rem',
      color: '#f59e0b',
      marginTop: '0.5rem',
    },
    contactSection: {
      background: '#f9fafb',
    },
    contactGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '2rem',
    },
    contactCard: {
      background: 'white',
      padding: '2.5rem',
      borderRadius: '20px',
      textAlign: 'center',
      boxShadow: '0 8px 25px rgba(236, 72, 153, 0.1)',
      transition: 'all 0.3s ease',
    },
    contactIcon: {
      width: '70px',
      height: '70px',
      background: 'linear-gradient(135deg, #ec4899, #db2777)',
      color: 'white',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    contactTitle: {
      fontWeight: '700',
      color: '#831843',
      fontSize: '1.2rem',
      marginBottom: '0.75rem',
    },
    contactText: {
      color: '#6b7280',
      lineHeight: 1.6,
    },
    footer: {
      background: '#831843',
      color: 'white',
      padding: '3rem 2rem 1.5rem',
    },
    footerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '2.5rem',
      marginBottom: '2rem',
    },
    footerTitle: {
      fontSize: '1.25rem',
      fontWeight: '700',
      marginBottom: '1rem',
    },
    footerText: {
      color: '#fce7f3',
      marginBottom: '1rem',
      lineHeight: 1.6,
    },
    footerLink: {
      color: '#fce7f3',
      display: 'block',
      marginBottom: '0.5rem',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
      textDecoration: 'none',
    },
    socialLinks: {
      display: 'flex',
      gap: '1rem',
      marginTop: '1rem',
    },
    socialIcon: {
      width: '45px',
      height: '45px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    footerBottom: {
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      paddingTop: '1.5rem',
      textAlign: 'center',
      color: '#fce7f3',
    },
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-15px); }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .nav-button:hover { color: #ec4899; }
          .book-button:hover { transform: scale(1.05); box-shadow: 0 12px 35px rgba(236, 72, 153, 0.4); }
          .cta-button:hover { transform: scale(1.05); box-shadow: 0 15px 40px rgba(236, 72, 153, 0.4); }
          .service-card:hover { transform: translateY(-10px); box-shadow: 0 15px 50px rgba(236, 72, 153, 0.2); border-color: #ec4899; }
          .service-card:hover .service-image { transform: scale(1.1); }
          .service-button:hover { transform: scale(1.03); }
          .gallery-item:hover .gallery-img { transform: scale(1.15); }
          .gallery-item:hover .gallery-overlay { opacity: 1; }
          .contact-card:hover { transform: translateY(-8px); box-shadow: 0 15px 40px rgba(236, 72, 153, 0.2); }
          .footer-link:hover { color: #ffffff; }
          .social-icon:hover { background: rgba(255, 255, 255, 0.2); transform: translateY(-3px); }
          @media (max-width: 768px) {
            .menu-toggle { display: block !important; }
            .nav-links { display: none !important; }
          }
        `}
      </style>

      {/* Header */}
      <header style={styles.header}>
        <nav style={styles.nav}>
          <div style={styles.logo}>
            <Sparkles size={28} />
            LUXE BEAUTY
          </div>
          <div style={styles.navLinks} className="nav-links">
            <button onClick={() => scrollToSection('home')} style={styles.navButton} className="nav-button">Home</button>
            <button onClick={() => scrollToSection('services')} style={styles.navButton} className="nav-button">Services</button>
            <button onClick={() => scrollToSection('gallery')} style={styles.navButton} className="nav-button">Gallery</button>
            <button onClick={() => scrollToSection('testimonials')} style={styles.navButton} className="nav-button">Reviews</button>
            <button onClick={() => scrollToSection('contact')} style={styles.navButton} className="nav-button">Contact</button>
            <button style={styles.bookButton} className="book-button">
              <Calendar size={18} />
              Book Now
            </button>
          </div>
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} style={styles.menuToggle} className="menu-toggle">
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" style={styles.heroSection}>
        <div style={styles.heroContent}>
          <div style={styles.heroText}>
            <h1 style={styles.heroTitle}>Experience Pure Luxury & Relaxation</h1>
            <p style={styles.heroSubtitle}>
              Indulge in premium beauty treatments and spa services designed to rejuvenate your body, mind, and soul. Your journey to wellness begins here.
            </p>
            <div style={styles.heroFeatures}>
              <div style={styles.feature}>
                <Heart size={18} />
                Expert Therapists
              </div>
              <div style={styles.feature}>
                <Sparkles size={18} />
                Premium Products
              </div>
              <div style={styles.feature}>
                <Star size={18} />
                5-Star Service
              </div>
            </div>
            <button style={styles.ctaButton} className="cta-button">
              <Calendar size={22} />
              Book Your Appointment
            </button>
          </div>
          <div style={styles.heroImage}>
            <img
              src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600&h=600&fit=crop"
              alt="Spa relaxation"
              style={styles.heroImg}
            />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" style={styles.section}>
        <h2 style={styles.sectionTitle}>Our Signature Services</h2>
        <p style={styles.sectionSubtitle}>
          Discover our range of luxurious treatments designed to enhance your natural beauty and promote inner peace
        </p>
        <div style={styles.servicesGrid}>
          {services.map((service, index) => (
            <div key={index} style={styles.serviceCard} className="service-card">
              <img src={service.image} alt={service.name} style={styles.serviceImage} className="service-image" />
              <div style={styles.serviceContent}>
                <span style={styles.serviceIcon}>{service.icon}</span>
                <h3 style={styles.serviceName}>{service.name}</h3>
                <p style={styles.serviceDesc}>{service.description}</p>
                <div style={styles.servicePrice}>{service.price}</div>
                <button style={styles.serviceButton} className="service-button">
                  Book Now
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" style={styles.section}>
        <h2 style={styles.sectionTitle}>Our Beautiful Space</h2>
        <p style={styles.sectionSubtitle}>
          Step into our serene sanctuary designed for your complete relaxation and rejuvenation
        </p>
        <div style={styles.galleryGrid}>
          {gallery.map((img, index) => (
            <div key={index} style={styles.galleryItem} className="gallery-item">
              <img src={img} alt={`Gallery ${index + 1}`} style={styles.galleryImg} className="gallery-img" />
              <div style={styles.galleryOverlay} className="gallery-overlay">
                View More
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" style={{...styles.section, ...styles.testimonialsSection}}>
        <h2 style={styles.sectionTitle}>What Our Clients Say</h2>
        <p style={styles.sectionSubtitle}>
          Read the experiences of our satisfied clients who trust us with their beauty and wellness needs
        </p>
        <div style={styles.testimonialsGrid}>
          {testimonials.map((testimonial, index) => (
            <div key={index} style={styles.testimonialCard}>
              <p style={styles.testimonialText}>"{testimonial.text}"</p>
              <div style={styles.testimonialAuthor}>{testimonial.name}</div>
              <div style={styles.rating}>
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={18} fill="#f59e0b" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{...styles.section, ...styles.contactSection}}>
        <h2 style={styles.sectionTitle}>Visit Us Today</h2>
        <p style={styles.sectionSubtitle}>
          Find us at our luxurious location or reach out to schedule your appointment
        </p>
        <div style={styles.contactGrid}>
          <div style={styles.contactCard} className="contact-card">
            <div style={styles.contactIcon}>
              <MapPin size={32} />
            </div>
            <h3 style={styles.contactTitle}>Location</h3>
            <p style={styles.contactText}>789 Wellness Avenue<br/>Beauty District, BD 67890</p>
          </div>
          <div style={styles.contactCard} className="contact-card">
            <div style={styles.contactIcon}>
              <Phone size={32} />
            </div>
            <h3 style={styles.contactTitle}>Phone</h3>
            <p style={styles.contactText}>(555) 234-5678<br/>Mon-Sun: 9AM - 8PM</p>
          </div>
          <div style={styles.contactCard} className="contact-card">
            <div style={styles.contactIcon}>
              <Mail size={32} />
            </div>
            <h3 style={styles.contactTitle}>Email</h3>
            <p style={styles.contactText}>hello@luxebeauty.com<br/>info@luxebeauty.com</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div>
            <h4 style={styles.footerTitle}>LUXE BEAUTY</h4>
            <p style={styles.footerText}>
              Your premier destination for luxury beauty treatments and spa services. We're committed to your wellness journey.
            </p>
            <div style={styles.socialLinks}>
              <div style={styles.socialIcon} className="social-icon">
                <Facebook size={22} />
              </div>
              <div style={styles.socialIcon} className="social-icon">
                <Instagram size={22} />
              </div>
              <div style={styles.socialIcon} className="social-icon">
                <Twitter size={22} />
              </div>
            </div>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Quick Links</h4>
            <a onClick={() => scrollToSection('home')} style={styles.footerLink} className="footer-link">Home</a>
            <a onClick={() => scrollToSection('services')} style={styles.footerLink} className="footer-link">Services</a>
            <a onClick={() => scrollToSection('gallery')} style={styles.footerLink} className="footer-link">Gallery</a>
            <a onClick={() => scrollToSection('testimonials')} style={styles.footerLink} className="footer-link">Reviews</a>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Services</h4>
            <a style={styles.footerLink} className="footer-link">Hair Styling</a>
            <a style={styles.footerLink} className="footer-link">Facial Treatments</a>
            <a style={styles.footerLink} className="footer-link">Massage Therapy</a>
            <a style={styles.footerLink} className="footer-link">Spa Packages</a>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Opening Hours</h4>
            <p style={styles.footerText}>Monday - Friday<br/>9:00 AM - 8:00 PM</p>
            <p style={styles.footerText}>Saturday - Sunday<br/>10:00 AM - 7:00 PM</p>
          </div>
        </div>
        <div style={styles.footerBottom}>
          <p>&copy; 2025 Luxe Beauty Salon & Spa. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Template3;